import { useState, useEffect, useCallback, useRef } from 'react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { API_ENDPOINTS } from '../config/api';
import { userAPI, holidayAPI, workStatusAPI } from '../services/api';

// Utility function to format names as "F. Surname"
const formatDisplayName = (firstName, lastName) => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}. ${lastName}`;
  }
  return firstName || lastName || '';
};

function useCalendarData(currentUser = null, onError = null, currentMonthRef = { current: new Date() }, vacationBalance = null) {
  const [workStatuses, setWorkStatuses] = useState([]);
  const [todayStatuses, setTodayStatuses] = useState([]); // Separate state for today's stats
  const [teamMembers, setTeamMembers] = useState([]);
  const [vacationBalanceForUser, setVacationBalanceForUser] = useState(vacationBalance || {});
  const [holidays, setHolidays] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nameMapping, setNameMapping] = useState({}); // Maps display names to full names
  const [userEmploymentData, setUserEmploymentData] = useState({}); // Maps display names to employment data
  // Tracks which years' holidays have already been fetched/requested, without
  // being reactive state - see the merged load effect below for why that matters.
  const fetchedHolidayYearsRef = useRef(new Set());

  // Mirror the App-level vacation balance (fetched once at login and refreshed
  // after every status change) instead of independently re-fetching the same
  // endpoint here - see loadData, which used to do exactly that.
  useEffect(() => {
    if (vacationBalance) {
      setVacationBalanceForUser(vacationBalance);
    }
  }, [vacationBalance]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch users for dashboard with employment date filtering
      const year = currentMonthRef.current.getFullYear();
      const month = currentMonthRef.current.getMonth() + 1; // JavaScript months are 0-indexed
      const users = await userAPI.getDashboardUsers(year, month);

      // Create display names and mapping
      const mapping = {};
      const employmentData = {};
      const displayNames = users.map(user => {
        const displayName = formatDisplayName(user.first_name, user.last_name);
        mapping[displayName] = user.first_name + " " + user.last_name;
        employmentData[displayName] = {
          employment_start_date: user.employment_start_date,
          employment_end_date: user.employment_end_date
        };
        return displayName;
      });

      // Sort with current user first, then alphabetically
      let sortedDisplayNames;
      const currentUserDisplay = currentUser ? formatDisplayName(currentUser) : null;

      if (currentUserDisplay) {
        sortedDisplayNames = displayNames.sort((a, b) => {
          if (a === currentUserDisplay) return -1;
          if (b === currentUserDisplay) return 1;
          return a.localeCompare(b);
        });
      } else {
        sortedDisplayNames = displayNames.sort((a, b) => a.localeCompare(b));
      }

      setTeamMembers(sortedDisplayNames);
      setNameMapping(mapping);
      setUserEmploymentData(employmentData);
      // vacationBalanceForUser is kept in sync from the vacationBalance prop
      // (see the effect above) instead of being fetched here separately.
    } catch (err) {
      setError(err.message);
      console.error('Failed to load initial data:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, currentMonthRef]);

  // Load holidays for a specific year
  const loadHolidays = useCallback(async (year) => {
    // Guard here, not just at each call site: loadHolidays is called
    // independently from both this hook's own load effect and from
    // Dashboard.js's month-change effect, so a once-per-caller check isn't
    // enough to prevent a duplicate fetch when both happen to run for the
    // same year.
    if (fetchedHolidayYearsRef.current.has(year)) {
      return;
    }
    fetchedHolidayYearsRef.current.add(year);

    try {
      const holidayData = await holidayAPI.getHolidays(year);

      // Convert to format expected by frontend (date string -> holiday object)
      const holidayMap = {};
      holidayData.forEach(holiday => {
        holidayMap[holiday.date] = {
          name: {
            hr: holiday.name_croatian,
            en: holiday.name
          }
        };
      });

      setHolidays(prev => ({ ...prev, [year]: holidayMap }));
    } catch (err) {
      console.error('Failed to load holidays:', err);
      // Don't set error state for holidays as it's not critical
      fetchedHolidayYearsRef.current.delete(year); // allow retrying a failed fetch
    }
  }, []);

  // Load today's statuses for stats (independent of calendar month)
  const loadTodayStatuses = useCallback(async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const statuses = await workStatusAPI.getWorkStatuses(today, today);

      // Convert backend format to frontend format with display names
      const formattedStatuses = statuses.map(status => ({
        user: formatDisplayName(status.first_name, status.last_name),
        date: status.date,
        status: status.status
      }));

      setTodayStatuses(formattedStatuses);
    } catch (err) {
      console.error('Failed to load today\'s statuses:', err);
    }
  }, []);

  // Load dashboard data once on mount (and again if the logged-in user
  // changes). This used to be two separate effects that each independently
  // fetched the same dashboard-users data (one of them via a second,
  // copy-pasted implementation), and the second one also listed `holidays`
  // state as a dependency while being the thing that updates it - so every
  // successful holiday fetch changed `holidays`'s identity and re-triggered
  // the whole effect a second time, re-fetching dashboard-users again too.
  // Tracking fetched years in a ref instead of reactive state avoids that
  // self-retrigger, and merging the two effects removes the duplicate fetch.
  useEffect(() => {
    loadData();
    loadTodayStatuses();
    loadHolidays(currentMonthRef.current.getFullYear()); // loadHolidays itself skips years already fetched
  }, [currentUser, loadData, loadTodayStatuses, loadHolidays, currentMonthRef]);

  // Load work statuses for a specific month
  const loadWorkStatusesForMonth = useCallback(async (month) => {
    try {
      const startDate = format(startOfMonth(month), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(month), 'yyyy-MM-dd');

      const statuses = await workStatusAPI.getWorkStatuses(startDate, endDate);

      // Convert backend format to frontend format with display names
      const formattedStatuses = statuses.map(status => ({
        user: formatDisplayName(status.first_name, status.last_name),
        date: status.date,
        status: status.status,
        vacation_source: status.vacation_source,
        attachment_name: status.attachment_name,
        attachment_mime_type: status.attachment_mime_type,
        attachment_size: status.attachment_size,
        attachment_uploaded_at: status.attachment_uploaded_at,
        attachment_url: status.attachment_url,
        work_status_id: status.id
      }));

      setWorkStatuses(formattedStatuses);
    } catch (err) {
      setError(err.message);
      console.error('Failed to load work statuses:', err);
    }
  }, []);

  const getStatusForUserAndDate = (user, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return workStatuses.find(s => s.user === user && s.date === dateStr);
  };

  // vacationBalanceForUser always reflects the currently-open year (2026, say),
  // kept in sync from the App-level balance. That's wrong to use for a
  // pre-flight check on a booking dated in a different, not-yet-open year
  // (e.g. pre-booking January/February 2027 while 2026 is still open) - it would
  // compare the request against a year that has nothing to do with the dates
  // being booked. Fetch the balance for whatever year the dates actually fall
  // in instead; the backend already computes this correctly for any year via
  // GetOrCreateVacationYear, current or future.
  const fetchBalanceForYear = async (year) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.VACATION_BALANCE}/${year}`, {
        credentials: 'include',
      });
      if (response.ok) {
        return await response.json();
      }
    } catch (err) {
      console.error('Failed to load vacation balance for year', year, err);
    }
    return null;
  };

  const updateStatus = async (dates, status, currentUser, t, options = {}) => {
    const {
      onVacationWarning = null,
      targetUser: targetUserDisplayName = null,
      isAdminEdit = false,
      attachment = null,
    } = options || {};
    try {
      // Handle both single date and array of dates
      const datesToUpdate = Array.isArray(dates) ? dates : [dates];
      const dateStrings = datesToUpdate.map(date => format(date, 'yyyy-MM-dd'));

      // In admin edit mode, targetUser will be the username of the user being edited
      // If not in admin edit mode, we're editing the current user
      const userToEdit = isAdminEdit && targetUserDisplayName ? targetUserDisplayName : currentUser;

      // Check vacation balance if setting vacation status
      // Always check vacation balance when setting vacation status
      if (status === 'vacation' && !isAdminEdit) {
        const newVacationDays = datesToUpdate.length;
        const bookingYear = datesToUpdate[0].getFullYear();

        // Use the balance for the year actually being booked, not whatever
        // vacationBalanceForUser currently holds (the currently-open year) -
        // those differ when pre-booking a future, not-yet-open year.
        const yearBalance = await fetchBalanceForYear(bookingYear);
        const currentYearRemaining = yearBalance
          ? (yearBalance.current_year_remaining || 0)
          : (vacationBalanceForUser?.current_year_remaining || 0);
        const carryoverRemaining = yearBalance
          ? (yearBalance.carryover_remaining || 0)
          : (vacationBalanceForUser?.carryover_remaining || 0);
        const totalRemaining = currentYearRemaining + carryoverRemaining;

        if (totalRemaining < newVacationDays) {
          // Use modal callback if provided, otherwise fallback to alert
          if (onVacationWarning) {
            onVacationWarning(totalRemaining, newVacationDays);
          } else {
            alert(t('insufficientVacationDays', { remaining: totalRemaining, requested: newVacationDays }));
          }
          return false;
        }
      }

      // Get the username from the display name for admin edit mode
      let username = null;
      if (isAdminEdit && targetUserDisplayName) {
        // Find the user's full name from the display name mapping
        const fullName = nameMapping[targetUserDisplayName];
        const currentDate = new Date(currentMonthRef.current);
        const year = currentDate.getFullYear();
        if (fullName) {
          // Use dashboard users (which includes users with employment end dates) instead of getAllUsers
          const month = currentDate.getMonth() + 1;
          const users = await userAPI.getDashboardUsers(year, month);
          const user = users.find(u => {
            const displayName = formatDisplayName(u.first_name, u.last_name);
            return displayName === targetUserDisplayName;
          });
          if (user) {
            username = user.username;
          }
        }

        if (!username) {
          console.error('Could not find username for target user:', targetUserDisplayName);
          return false;
        }
        const vacationYearResponse = await fetch(`${API_ENDPOINTS.ADMIN_USERNAMES}/${username}/vacation-year?year=${year}`, {
          credentials: 'include',
        });

        if (vacationYearResponse.ok) {
          const data = await vacationYearResponse.json();
          const newVacationDays = datesToUpdate.length;
          const currentYearRemaining = (data?.vacation_year?.vacation_contract - data?.vacation_year?.vacation_used) || 0;
          const carryoverRemaining = (data?.vacation_year?.vacation_carryover - data?.vacation_year?.carryover_used) || 0;
          const totalRemaining = currentYearRemaining + carryoverRemaining;

          if (status === 'vacation' && totalRemaining < newVacationDays) {
            // Use modal callback if provided, otherwise fallback to alert
            if (onVacationWarning) {
              onVacationWarning(totalRemaining, newVacationDays, true);
            } else {
              alert(t('insufficientVacationDays', { remaining: totalRemaining, requested: newVacationDays }));
            }
            return false;
          }
        }
      }

      if (status === null) {
        // Delete status(es)
        if (isAdminEdit && username) {
          // Use admin endpoints for deleting other users' statuses
          if (dateStrings.length === 1) {
            await workStatusAPI.adminDeleteWorkStatus(dateStrings[0], username);
          } else {
            await workStatusAPI.adminDeleteWorkStatusBatch(dateStrings, username);
          }
        } else {
          // Use regular endpoints for current user
          if (dateStrings.length === 1) {
            await workStatusAPI.deleteWorkStatus(dateStrings[0]);
          } else {
            await workStatusAPI.deleteWorkStatusBatch(dateStrings);
          }
        }

        // After deletion, refresh vacation balance to get updated values
        await refreshVacationBalance();
      } else {
        // Set status(es)
        if (isAdminEdit && username) {
          // Use admin endpoints for setting other users' statuses
          if (dateStrings.length === 1) {
            await workStatusAPI.adminSetWorkStatus(dateStrings[0], status, username, { attachment });
          } else {
            await workStatusAPI.adminSetWorkStatusBatch(dateStrings, status, username, { attachment });
          }
        } else {
          // Use regular endpoints for current user
          if (dateStrings.length === 1) {
            await workStatusAPI.setWorkStatus(dateStrings[0], status, { attachment });
          } else {
            await workStatusAPI.setWorkStatusBatch(dateStrings, status, { attachment });
          }
        }
      }

      // Update local state optimistically instead of reloading everything
      if (status === null) {
        // Remove statuses locally
        setWorkStatuses(prev => prev.filter(s =>
          !(s.user === userToEdit && dateStrings.includes(s.date))
        ));
      } else {
        // Update statuses locally
        setWorkStatuses(prev => {
          const updated = [...prev];
          dateStrings.forEach(dateStr => {
            const existingIndex = updated.findIndex(s => s.user === userToEdit && s.date === dateStr);
            const existingEntry = existingIndex !== -1 ? updated[existingIndex] : null;

            const statusEntry = {
              user: userToEdit,
              date: dateStr,
              status,
              attachment_url: existingEntry?.attachment_url || null,
              attachment_name: existingEntry?.attachment_name || null,
              attachment_mime_type: existingEntry?.attachment_mime_type || null,
              attachment_size: existingEntry?.attachment_size || null,
            };

            if (attachment) {
              statusEntry.attachment_url = null;
              statusEntry.attachment_name = attachment.name;
              statusEntry.attachment_mime_type = attachment.type;
              statusEntry.attachment_size = attachment.size;
            } else if (!['sick', 'other'].includes(status)) {
              statusEntry.attachment_url = null;
              statusEntry.attachment_name = null;
              statusEntry.attachment_mime_type = null;
              statusEntry.attachment_size = null;
            }

            if (existingIndex !== -1) {
              updated[existingIndex] = statusEntry;
            } else {
              updated.push(statusEntry);
            }
          });
          return updated;
        });
      }

      // Refresh today's stats if any of the updated dates is today
      const today = format(new Date(), 'yyyy-MM-dd');
      if (dateStrings.includes(today)) {
        await loadTodayStatuses();
      }

      // Refresh vacation balance from backend to ensure header shows accurate data
      await refreshVacationBalance();

      // Reload month data to ensure attachment metadata stays in sync
      if (loadWorkStatusesForMonth) {
        await loadWorkStatusesForMonth(currentMonthRef.current);
      }

      return true;
    } catch (err) {
      console.error('Failed to update status:', err);

      // Handle specific error types
      if (err.errorType === 'no_vacation_days') {
        // Get actual remaining days from the year actually being booked, same
        // reasoning as the pre-flight check above.
        const datesForError = Array.isArray(dates) ? dates : [dates];
        const bookingYear = datesForError[0]?.getFullYear();
        const yearBalance = bookingYear ? await fetchBalanceForYear(bookingYear) : null;
        const currentYearRemaining = yearBalance
          ? (yearBalance.current_year_remaining || 0)
          : (vacationBalanceForUser?.current_year_remaining || 0);
        const carryoverRemaining = yearBalance
          ? (yearBalance.carryover_remaining || 0)
          : (vacationBalanceForUser?.carryover_remaining || 0);
        const totalRemaining = currentYearRemaining + carryoverRemaining;
        const newVacationDays = datesForError.length;

        // Use modal callback if provided, otherwise fallback to alert
        if (onVacationWarning) {
          onVacationWarning(totalRemaining, newVacationDays);
        } else {
          alert(t('insufficientVacationDays', { remaining: totalRemaining, requested: newVacationDays }));
        }
      } else {
        // For other errors, show a generic message
        setError(err.message);
        if (onError) {
          onError(err.message);
        } else {
          alert('Failed to update status: ' + err.message);
        }
      }

      return false;
    }
  };

  // Calculate team stats for today using dedicated today statuses
  const getTeamStats = () => {
    return {
      inOffice: todayStatuses.filter(s => s.status === 'office').length,
      workingFromHome: todayStatuses.filter(s => s.status === 'home').length,
      onLeave: todayStatuses.filter(s => ['vacation', 'sick', 'other'].includes(s.status)).length,
      total: teamMembers.length
    };
  };

  // Helper function to get full name from display name
  const getFullNameFromDisplay = (displayName) => {
    return nameMapping[displayName] || displayName;
  };

  // Check if a date is a Croatian holiday
  const isCroatianHoliday = (date) => {
    const year = date.getFullYear();
    const dateStr = format(date, 'yyyy-MM-dd');
    return holidays[year] && holidays[year][dateStr];
  };

  // Get holiday name for a date
  const getHolidayName = (date, language = 'hr') => {
    const year = date.getFullYear();
    const dateStr = format(date, 'yyyy-MM-dd');
    const holiday = holidays[year] && holidays[year][dateStr];
    return holiday ? holiday.name[language] : null;
  };

  // Function to refresh vacation balance for current user
  const refreshVacationBalance = useCallback(async () => {
    if (!currentUser) return;

    try {
      const response = await fetch(API_ENDPOINTS.VACATION_BALANCE, {
        credentials: 'include'
      });

      if (response.ok) {
        const balanceData = await response.json();
        setVacationBalanceForUser(balanceData);
      }
    } catch (err) {
      console.error('Failed to refresh vacation balance:', err);
    }
  }, [currentUser]);

  return {
    teamMembers,
    workStatuses,
    holidays,
    loading,
    error,
    getStatusForUserAndDate,
    updateStatus,
    getTeamStats,
    vacationBalanceForUser,
    loadWorkStatusesForMonth,
    loadHolidays,
    isCroatianHoliday,
    getHolidayName,
    nameMapping,
    getFullNameFromDisplay,
    loadData,
    refreshVacationBalance,
    userEmploymentData,
  };
}

export default useCalendarData;
